import { useMemo } from 'react';
import { Timezone } from 'vopay';
import { AccountAndBillingHostProps, BillingApi } from 'shared/accountAndBilling';
import { api } from '@/services/api.config';
import { usePartnerData } from '@/providers/PartnerData.provider';

// PartnerData carries no timezone; date display defaults to VoPay's. Low-stakes — only affects how
// invoice dates render, and a backend timezone field would replace this if one is ever added.
const DEFAULT_TIMEZONE: Timezone = 'America/Vancouver';

// The partner's billing calls, built from its own api instance — partner's side of the shared
// feature's host-varying-endpoint interface (the client hits /account/*, the partner hits /partner/*).
// Static (only the module-singleton api), so it lives at module scope rather than being rebuilt per
// render.
const billingApi: BillingApi = {
  fetchInvoices: async (params) => {
    const res = await api.fetchPartnerInvoices(params);

    // The CL keeps the backend's `PartnerInvoices` name; shared's BillingApi expects `Invoices`.
    return { Success: res.Success, ErrorMessage: res.ErrorMessage, Invoices: res.PartnerInvoices };
  },
  fetchInvoicePdf: api.fetchPartnerInvoicePdf,
  fetchStatement: async ({ InvoiceID }) => {
    const res = await api.fetchPartnerStatementPdf({ InvoiceID });

    // Partner statements are PDF (client's are CSV); the format travels with the bytes.
    return { Success: res.Success, ErrorMessage: res.ErrorMessage, data: res.StatementPDF, format: 'pdf' };
  },
  fetchDefaultPaymentMethod: api.fetchPartnerDefaultPaymentMethod,
  setDefaultPaymentMethod: api.setPartnerDefaultPaymentMethod,
};

// The one place partner-dashboard maps its own user/permission model onto what the shared Account &
// Billing feature takes as props. Mirrors client's useBillingHostProps. Partners are bank-only, can't
// add billing methods, and see one of the two statement/invoice tabs depending on partner type.
export default function usePartnerBillingHostProps(): AccountAndBillingHostProps {
  const {
    response: { loginUsername, partnerType, isRoleAdmin },
  } = usePartnerData();

  return useMemo(
    () => ({
      viewer: { username: loginUsername, timezone: DEFAULT_TIMEZONE },
      account: {
        // Partner-dashboard has no country for the partner's billing account: neither the session
        // nor the bank-account response carries one (the client portal reads its account's Country,
        // partners have no equivalent). Both the displayed account number and the drawer's address
        // "Country" line now derive per-row from each bank account's own routing fields (see
        // getFormattedAccountNumber and buildAccountRows), so this injected value is only a fallback
        // for a row that reveals neither — a credit card or a foreign/IBAN account. Left blank rather
        // than a guessed 'CA', so those degrade to '-' instead of a false "Canada".
        country: '',
        isFranchise: false,
        features: { creditCard: false, elinxDirectAdd: false },
      },
      userPermissions: { canManageBilling: isRoleAdmin, canAddBilling: false },
      billingApi,
      // Referral partners download statements; VAR/Technology download invoices.
      secondTab: partnerType === 'Referral' ? 'statement' : 'invoice',
    }),
    [loginUsername, partnerType, isRoleAdmin],
  );
}
